// Shared admin actions for one referral-commission row: mark paid + void,
// each behind a ConfirmDialog with an optional note. Only legal from the
// "accrued" state (accrued -> paid | accrued -> void) — the server enforces
// this too (409 conflict), so a stale row (already flipped in another tab)
// can't be double-flipped from here.

import { useState } from "react";
import { fieldErrorClass } from "@ahcut/ui/recipes";
import { Button, Field } from "@/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { markAdminCommissionPaid, voidAdminCommission } from "@/lib/admin";
import type { AdminCommission } from "@/lib/types";

type PendingAction = "paid" | "void" | null;

export function CommissionAdminActions({
  commission,
  onDone,
}: {
  commission: AdminCommission;
  /** Called after a successful action so the parent list refetches. */
  onDone: () => void;
}) {
  const [pending, setPending] = useState<PendingAction>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accrued = commission.status === "accrued";

  function open(action: Exclude<PendingAction, null>) {
    setNote("");
    setError(null);
    setPending(action);
  }

  async function run() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      if (pending === "paid") {
        await markAdminCommissionPaid(commission.id, note);
      } else {
        await voidAdminCommission(commission.id, note);
      }
      setPending(null);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="quiet"
        size="sm"
        onClick={() => open("paid")}
        disabled={!accrued}
        title={accrued ? "Tandai komisi ini dibayar" : "Hanya komisi berstatus Terkumpul yang bisa ditandai"}
      >
        Tandai dibayar
      </Button>
      <Button
        variant="danger"
        size="sm"
        onClick={() => open("void")}
        disabled={!accrued}
        title={accrued ? "Batalkan komisi ini" : "Hanya komisi berstatus Terkumpul yang bisa dibatalkan"}
      >
        Void
      </Button>

      <ConfirmDialog
        open={pending === "paid"}
        title="Tandai komisi dibayar?"
        confirmLabel="Tandai dibayar"
        busy={busy}
        onConfirm={run}
        onCancel={() => setPending(null)}
      >
        <p>Komisi ditandai dibayar. Payout sendiri dilakukan manual di luar sistem ini.</p>
        <Field
          label="Catatan (opsional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="mis. transfer 18 Jul via BCA"
        />
        {error && <p className={fieldErrorClass}>{error}</p>}
      </ConfirmDialog>

      <ConfirmDialog
        open={pending === "void"}
        title="Batalkan komisi ini?"
        confirmLabel="Void"
        danger
        busy={busy}
        onConfirm={run}
        onCancel={() => setPending(null)}
      >
        <p>Komisi dibatalkan permanen — dipakai saat order-nya direfund atau referral tidak sah.</p>
        <Field
          label="Catatan (opsional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="mis. order direfund"
        />
        {error && <p className={fieldErrorClass}>{error}</p>}
      </ConfirmDialog>
    </div>
  );
}
